// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
// Pointer-driven channel→pane drag for the series toggle legends.
// Grab a channel label in the Data Traces legend (or a chip in the drawer's
// summary strip — chips may carry a comma-separated key list that moves the
// whole EGT/CHT bank) and drop it on the primary (temperature) or secondary
// graph — or on the other legend group — to move that channel's trace between
// the two chart panes. Dropping a channel on the pane it already lives in
// turns the channel on instead. Mirrors the pointer pattern in
// aircraft-fleet-reorder.js (8px activation distance, cloned preview chip,
// suppressed post-drag click).

export function seriesPaneDropTargetAtPoint(targets, clientX, clientY) {
  return targets.find(({ rect }) => (
    clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom
  )) ?? null;
}

export function paneZoneHtml(pane, isCurrentPane) {
  const verb = isCurrentPane ? "Show on" : "Move to";
  return `<div class="series-pane-drop-zone is-${pane}" data-pane-drop="${pane}"><span>${verb} ${pane} graph</span></div>`;
}

export function createSeriesPaneDrag({
  seriesPaneForKey,
  moveSeriesToPane,
  chartSelectorByContainerId = {
    seriesControls: "#flightChart",
    explorerSeriesControls: "#explorerChart",
    seriesChipStrip: "#flightChart",
    explorerSeriesChipStrip: "#explorerChart",
  },
}) {
  const activationDistancePx = 8;
  const boundContainers = new WeakSet();
  let windowListenersBound = false;
  let pointerDrag = null;
  let suppressNextClick = false;

  function chartWrapForContainer(container) {
    const selector = chartSelectorByContainerId[container?.id];
    const canvas = selector ? document.querySelector(selector) : null;
    return canvas?.closest(".chart-wrap") ?? null;
  }

  function dragLabelFromTarget(target) {
    if (target?.closest?.(".series-pane-move")) return null;
    return target?.closest?.("label[data-series-drag]") ?? null;
  }

  function createDropZones() {
    const chartWrap = pointerDrag?.chartWrap;
    if (!chartWrap) return;
    const currentPane = seriesPaneForKey(pointerDrag.key);
    chartWrap.insertAdjacentHTML(
      "beforeend",
      paneZoneHtml("primary", currentPane === "primary") + paneZoneHtml("secondary", currentPane === "secondary"),
    );
  }

  function removeDropZones() {
    document.querySelectorAll(".series-pane-drop-zone").forEach((zone) => zone.remove());
  }

  function dropTargets() {
    if (!pointerDrag) return [];
    const elements = [
      ...(pointerDrag.chartWrap?.querySelectorAll(".series-pane-drop-zone[data-pane-drop]") ?? []),
      ...pointerDrag.container.querySelectorAll(".series-control-group[data-pane-drop]"),
    ];
    return elements.map((element) => ({
      pane: element.dataset.paneDrop,
      element,
      rect: element.getBoundingClientRect(),
    }));
  }

  function clearDropHighlights() {
    document.querySelectorAll(".is-series-drop-active").forEach((element) => {
      element.classList.remove("is-series-drop-active");
    });
  }

  function updateDragPreviewPosition(clientX, clientY) {
    if (!pointerDrag?.preview) return;
    const nextLeft = Math.round(clientX - pointerDrag.offsetX);
    const nextTop = Math.round(clientY - pointerDrag.offsetY);
    pointerDrag.preview.style.transform = `translate3d(${nextLeft}px, ${nextTop}px, 0)`;
  }

  function createDragPreview(label, activationEvent) {
    if (!label || !pointerDrag) return;
    const rect = label.getBoundingClientRect();
    const preview = label.cloneNode(true);
    preview.classList.add("series-pane-drag-preview");
    preview.setAttribute("aria-hidden", "true");
    preview.style.width = `${Math.round(rect.width)}px`;
    pointerDrag.preview = preview;
    document.body.append(preview);
    updateDragPreviewPosition(activationEvent.clientX, activationEvent.clientY);
  }

  function resetPointerDrag() {
    pointerDrag?.preview?.remove?.();
    pointerDrag = null;
    removeDropZones();
    clearDropHighlights();
    document.body.classList.remove("is-series-pane-dragging");
  }

  function onPointerDown(event) {
    if (event.button !== 0) return;
    const label = dragLabelFromTarget(event.target);
    if (!label) return;
    const rect = label.getBoundingClientRect();
    pointerDrag = {
      key: label.dataset.seriesDrag ?? "",
      container: event.currentTarget,
      chartWrap: chartWrapForContainer(event.currentTarget),
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      active: false,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
      preview: null,
    };
  }

  function onPointerMove(event) {
    if (!pointerDrag || event.pointerId !== pointerDrag.pointerId) return;

    if (!pointerDrag.active) {
      const distance = Math.hypot(event.clientX - pointerDrag.startX, event.clientY - pointerDrag.startY);
      if (distance < activationDistancePx) return;
      pointerDrag.active = true;
      document.body.classList.add("is-series-pane-dragging");
      createDropZones();
      const label = pointerDrag.container.querySelector(`label[data-series-drag="${CSS.escape(pointerDrag.key)}"]`);
      createDragPreview(label, event);
    }

    event.preventDefault();
    updateDragPreviewPosition(event.clientX, event.clientY);
    clearDropHighlights();
    const target = seriesPaneDropTargetAtPoint(dropTargets(), event.clientX, event.clientY);
    target?.element.classList.add("is-series-drop-active");
  }

  function finishPointerDrag(event) {
    if (!pointerDrag || event.pointerId !== pointerDrag.pointerId) return;
    if (!pointerDrag.active) {
      resetPointerDrag();
      return;
    }

    event.preventDefault();
    // The browser's synthetic post-drag click (if any) fires before this
    // timeout, so the flag never lingers to eat a later legitimate click.
    suppressNextClick = true;
    setTimeout(() => { suppressNextClick = false; }, 0);
    const key = pointerDrag.key;
    const target = seriesPaneDropTargetAtPoint(dropTargets(), event.clientX, event.clientY);
    resetPointerDrag();
    if (key && target) {
      moveSeriesToPane(key, target.pane);
    }
  }

  function cancelPointerDrag(event) {
    if (!pointerDrag || (event && event.pointerId !== pointerDrag.pointerId)) return;
    resetPointerDrag();
  }

  function onClickCapture(event) {
    if (!suppressNextClick) return;
    suppressNextClick = false;
    event.preventDefault();
    event.stopPropagation();
  }

  // Containers are rebuilt via innerHTML on every setupSeriesControls pass, so
  // listeners delegate from the stable container element and bind only once.
  // Click suppression listens on window: a drop usually lands on the chart,
  // outside the legend container, and the synthetic click follows it there.
  function bindSeriesPaneDrag(container) {
    if (!container || boundContainers.has(container)) return;
    boundContainers.add(container);
    container.addEventListener("pointerdown", onPointerDown);
    if (windowListenersBound) return;
    windowListenersBound = true;
    window.addEventListener("click", onClickCapture, true);
    window.addEventListener("pointermove", onPointerMove, { passive: false });
    window.addEventListener("pointerup", finishPointerDrag);
    window.addEventListener("pointercancel", cancelPointerDrag);
  }

  return {
    bindSeriesPaneDrag,
  };
}
